import React, { useState, useEffect, useRef, useCallback } from 'react';
import { LockClosedIcon, CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';

// ── Types ────────────────────────────────────────────────────────────────────

interface PaymentSessionData {
  registrationId: string;
  primarySponsor: string;
  totalAmount: number;
  contactInfo: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
  billingInfo: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    address: string;
    city: string;
    state: string;
    zipCode: string;
  };
  eventYear: number;
}

declare global {
  interface Window {
    ClearentSDK: any;
    ClearentTokenSuccess: (raw: string, json: any) => void;
    ClearentTokenError: (raw: string, json: any) => void;
  }
}

// ── Constants ────────────────────────────────────────────────────────────────

const SESSION_KEY = 'cc_payment_session';

// ── Component ────────────────────────────────────────────────────────────────

const PaymentPage: React.FC = () => {
  const [sessionData, setSessionData] = useState<PaymentSessionData | null>(null);
  const [sdkConfig, setSdkConfig] = useState<{ pk: string; baseUrl: string; sdkUrl: string } | null>(null);
  const [sdkReady, setSdkReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [transactionResult, setTransactionResult] = useState<any>(null);
  const sdkLoadedRef = useRef(false);

  // ── 1. Load session data ──────────────────────────────────────────────────

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      if (!raw) {
        setError('No payment session found. Please return to your registration confirmation and click "Complete Payment" again.');
        return;
      }
      const parsed = JSON.parse(raw) as PaymentSessionData;
      setSessionData(parsed);
    } catch {
      setError('Unable to load payment details. Please return to your confirmation page and try again.');
    }
  }, []);

  // ── 2. Fetch SDK config from backend ─────────────────────────────────────

  useEffect(() => {
    if (!sessionData) return;
    (async () => {
      try {
        const res = await fetch('/api/clearent-config');
        if (!res.ok) throw new Error('Failed to load payment configuration');
        const json = await res.json();
        if (!json?.config?.pk) throw new Error('Payment configuration incomplete');
        setSdkConfig(json.config);
      } catch (e: any) {
        setError(e.message || 'Unable to initialize payment form. Please try again.');
      }
    })();
  }, [sessionData]);

  // ── 3. Load JSSDK script & init ───────────────────────────────────────────

  const handleToken = useCallback(
    async (jwt: string) => {
      if (!sessionData) return;
      setSubmitting(true);
      setError(null);

      try {
        const res = await fetch('/api/process-payment', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            registrationId: sessionData.registrationId,
            amount: String(Number(sessionData.totalAmount).toFixed(2)),
            paymentJwt: jwt,
          }),
        });

        const result = await res.json();

        if (!res.ok) {
          throw new Error(result?.error || result?.message || 'Payment failed. Please try again.');
        }

        const tx = result?.payload?.transaction;
        if (!tx || tx.result !== 'APPROVED') {
          throw new Error(tx?.['display-message'] || 'Payment was not approved. Please try again.');
        }

        // Clear session so payment can't be replayed
        sessionStorage.removeItem(SESSION_KEY);
        setTransactionResult(tx);
        setSuccess(true);
      } catch (e: any) {
        setError(e.message || 'Payment processing failed. Please try again.');
        setSubmitting(false);
      }
    },
    [sessionData]
  );

  useEffect(() => {
    if (!sdkConfig || sdkLoadedRef.current) return;
    sdkLoadedRef.current = true;

    // Register global callbacks BEFORE loading the script
    window.ClearentTokenSuccess = (_raw: string, json: any) => {
      const jwt = json?.payload?.['mobile-jwt']?.jwt;
      if (jwt) {
        handleToken(jwt);
      } else {
        setError('Invalid token response from payment processor. Please try again.');
        setSubmitting(false);
      }
    };

    window.ClearentTokenError = (_raw: string, json: any) => {
      const msg =
        json?.payload?.error?.['error-message'] ||
        json?.message ||
        'Card information could not be verified. Please check your details and try again.';
      setError(msg);
      setSubmitting(false);
    };

    const script = document.createElement('script');
    script.src = sdkConfig.sdkUrl;
    script.async = true;
    script.onload = () => {
      try {
        window.ClearentSDK.init({
          baseUrl: sdkConfig.baseUrl,
          pk: sdkConfig.pk,
          styles:
            '.form-control{color:#374151;font-size:16px;border:1px solid #d1d5db;border-radius:8px;padding:10px 12px;}' +
            '.form-control:focus{border-color:#2563eb;box-shadow:0 0 0 3px rgba(37,99,235,0.15);}',
        });
        setSdkReady(true);
      } catch (e: any) {
        setError('Failed to initialize payment form. Please refresh and try again.');
      }
    };
    script.onerror = () => {
      setError('Failed to load payment form. Please check your connection and refresh.');
    };
    document.head.appendChild(script);

    return () => {
      // Cleanup globals on unmount
      delete (window as any).ClearentTokenSuccess;
      delete (window as any).ClearentTokenError;
    };
  }, [sdkConfig, handleToken]);

  // ── 4. Submit ─────────────────────────────────────────────────────────────

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!sdkReady || submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      window.ClearentSDK.getPaymentToken();
    } catch {
      setError('Unable to submit payment. Please refresh and try again.');
      setSubmitting(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (success && transactionResult) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
          <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Approved!</h1>
          <p className="text-gray-600 mb-6">
            Your registration for the Champions Classic {sessionData?.eventYear} is confirmed.
          </p>
          <div className="bg-gray-50 rounded-xl p-4 text-left text-sm space-y-2 mb-6">
            {transactionResult['authorization-code'] && (
              <div className="flex justify-between">
                <span className="text-gray-500">Authorization</span>
                <span className="font-mono font-medium">{transactionResult['authorization-code']}</span>
              </div>
            )}
            {transactionResult['last-four'] && (
              <div className="flex justify-between">
                <span className="text-gray-500">Card</span>
                <span className="font-medium">···· {transactionResult['last-four']}</span>
              </div>
            )}
            {transactionResult.amount && (
              <div className="flex justify-between">
                <span className="text-gray-500">Amount</span>
                <span className="font-medium">
                  ${Number(transactionResult.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
              </div>
            )}
            {sessionData?.registrationId && (
              <div className="flex justify-between">
                <span className="text-gray-500">Confirmation</span>
                <span className="font-mono font-medium">{sessionData.registrationId}</span>
              </div>
            )}
          </div>
          <p className="text-xs text-gray-400">You may close this tab.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-800 to-blue-700 text-white py-6 px-4">
        <div className="max-w-xl mx-auto flex items-center gap-3">
          <LockClosedIcon className="h-6 w-6 flex-shrink-0" />
          <div>
            <h1 className="text-xl font-bold">Secure Payment</h1>
            <p className="text-blue-200 text-sm">Champions Classic {sessionData?.eventYear ?? new Date().getFullYear()}</p>
          </div>
          {sessionData?.totalAmount != null && (
            <div className="ml-auto text-right">
              <p className="text-xs text-blue-200 uppercase tracking-wide">Amount Due</p>
              <p className="text-2xl font-bold">
                ${Number(sessionData.totalAmount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-xl mx-auto p-4 py-8">
        {/* Registration summary */}
        {sessionData && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6 text-sm text-gray-700">
            <p className="font-semibold text-gray-900 mb-1">{sessionData.primarySponsor}</p>
            <p className="text-gray-500">{sessionData.contactInfo.email}</p>
            <p className="text-gray-400 text-xs mt-1">Ref: {sessionData.registrationId}</p>
          </div>
        )}

        {/* Error banner */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex gap-3 items-start">
            <ExclamationCircleIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-700 font-medium text-sm">Payment Error</p>
              <p className="text-red-600 text-sm mt-0.5">{error}</p>
            </div>
          </div>
        )}

        {/* Loading state */}
        {!error && (!sessionData || !sdkReady) && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
            <div className="h-10 w-10 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-500 text-sm">Loading secure payment form…</p>
          </div>
        )}

        {/* Payment form */}
        {!error && sdkReady && (
          <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-base font-semibold text-gray-800 mb-4">Card Information</h2>

            {/* JSSDK injects into this div */}
            <div id="payment-form" className="mb-6" />

            {submitting && (
              <div className="flex items-center gap-2 text-blue-600 text-sm mb-4">
                <div className="h-4 w-4 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
                Processing payment…
              </div>
            )}

            <button
              type="submit"
              disabled={submitting || !sdkReady}
              className="w-full bg-blue-700 hover:bg-blue-800 disabled:bg-blue-300 text-white font-semibold py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2"
            >
              <LockClosedIcon className="h-4 w-4" />
              {submitting ? 'Processing…' : `Pay $${Number(sessionData?.totalAmount ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`}
            </button>

            <p className="text-xs text-gray-400 text-center mt-3">
              Your card details are encrypted and processed securely by Xplor/Clearent. We never store your card number.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export { SESSION_KEY };
export default PaymentPage;
